import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import OneSignalPlugin from 'onesignal-cordova-plugin';
import { useEffect } from 'react';
import { telemetry } from '../../../services/backend';
import { captureEvent } from '../../../services/event-capture';
import {
  transitionToNextRoute,
  transitionToRawURL,
} from '../../../services/route/route-navigation';
import {
  logNotificationPermissionResult,
  logPermissionSnapshot,
} from './log-current-permissions';

const OneSignal = OneSignalPlugin;

// Builds below this ship the OneSignal 3 SDK, which has no native counterpart for the
// user-model actions used here. Those builds are handled by OneSignal.jsx instead.
const MINIMUM_USER_MODEL_APP_VERSION = 4;

export const GET_APP_INFO_TIMEOUT_MS = 5000;

let initializationPromise: Promise<boolean> | null = null;

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const getPlatformSafe = (): string => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'unknown';
  }
};

const reportOneSignalInitResult = ({
  ok,
  reason,
  platform,
  appVersion,
  message,
  pending,
}: {
  ok: boolean;
  reason?: string;
  platform: string;
  appVersion?: string;
  message?: string;
  pending?: boolean;
}) => {
  const payload = {
    ok,
    platform,
    ...(reason !== undefined ? { reason } : {}),
    ...(appVersion !== undefined ? { appVersion } : {}),
    ...(message !== undefined ? { message } : {}),
    ...(pending !== undefined ? { pending } : {}),
  };
  captureEvent({
    type: 'app: onesignal init result',
    payload,
  });
  if (!ok) {
    // eslint-disable-next-line no-console -- HyperDX browser console capture
    console.error('app: onesignal init result', payload);
  }
};

const reportUnexpectedOneSignalError = (reason: string, error: unknown) => {
  const payload = {
    ok: false,
    reason,
    platform: getPlatformSafe(),
    message: errorMessage(error),
  };
  captureEvent({
    type: 'app: onesignal init failed',
    payload,
  });
  // eslint-disable-next-line no-console -- HyperDX browser console capture
  console.error('app: onesignal init failed', payload);
};

/**
 * Initialises OneSignal once per app load, independent of authentication, so that every install
 * gets a subscription record and can receive the notification click callback. Resolves false on
 * platforms and builds where the user-model SDK is unavailable.
 */
export const initializeOneSignal = (): Promise<boolean> => {
  if (!initializationPromise) {
    initializationPromise = (async () => {
      const platform = getPlatformSafe();
      const oneSignalAppID = window?.DECKEE_CONFIG?.oneSignalAppID;

      if (!Capacitor.isNativePlatform()) {
        reportOneSignalInitResult({
          ok: false,
          reason: 'not-native',
          platform,
        });
        return false;
      }

      if (!oneSignalAppID) {
        reportOneSignalInitResult({
          ok: false,
          reason: 'missing-app-id',
          platform,
        });
        return false;
      }

      let appVersion: string | undefined;
      // App.getInfo is a local native read and should return quickly. A hang is
      // reported for ops, but we keep waiting so a slow/late result cannot
      // disable OneSignal for the rest of the session.
      const getInfoTimeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
        reportOneSignalInitResult({
          ok: false,
          reason: 'app-info-timeout',
          platform,
          message: 'App.getInfo timed out',
          pending: true,
        });
      }, GET_APP_INFO_TIMEOUT_MS);

      try {
        const { version }: { version: string } = await App.getInfo();
        appVersion = version;
      } catch (error) {
        reportOneSignalInitResult({
          ok: false,
          reason: 'app-info-failed',
          platform,
          appVersion,
          message: errorMessage(error),
        });
        // Allow a later effect to retry after a transient startup failure.
        initializationPromise = null;
        return false;
      } finally {
        clearTimeout(getInfoTimeoutId);
      }

      const majorVersion = parseInt(
        appVersion.match(/^(\d+)\./)?.[1] ?? '',
        10,
      );

      if (Number.isNaN(majorVersion)) {
        reportOneSignalInitResult({
          ok: false,
          reason: 'unparseable-app-version',
          platform,
          appVersion,
        });
        return false;
      }

      if (majorVersion < MINIMUM_USER_MODEL_APP_VERSION) {
        reportOneSignalInitResult({
          ok: false,
          reason: 'unsupported-app-version',
          platform,
          appVersion,
        });
        return false;
      }

      try {
        // initialize() returns void; there is nothing useful to await. Native readiness is
        // handled by the SDK internally, and login/permission calls queue until it is ready.
        OneSignal.initialize(oneSignalAppID);
      } catch (error) {
        reportOneSignalInitResult({
          ok: false,
          reason: 'initialize-failed',
          platform,
          appVersion,
          message: errorMessage(error),
        });
        initializationPromise = null;
        return false;
      }

      reportOneSignalInitResult({
        ok: true,
        platform,
        appVersion,
      });
      return true;
    })();
  }

  return initializationPromise;
};

export const resetOneSignalInitializationForTests = (): void => {
  initializationPromise = null;
};

export function useOneSignalSetup(currentMember: {
  id?: string | number | undefined;
  isOnBoardingComplete: boolean | undefined;
}) {
  const { id: currentMemberId, isOnBoardingComplete } = currentMember || {};

  useEffect(() => {
    let isActive = true;

    const handleNotificationClick = (event: unknown) => {
      const { notification } = (event ?? {}) as {
        notification?: {
          additionalData?: { route?: string; routeParams?: unknown };
        };
      };
      const route = notification?.additionalData?.route;
      const routeParams = notification?.additionalData?.routeParams;

      if (!route) return;

      // A route containing a slash is a URL path; anything else is a configured route name.
      // These navigation helpers hold their own history instance, which matters because this
      // hook is mounted outside the Router and so cannot use useHistory.
      if (route.includes('/')) {
        transitionToRawURL(route);
      } else {
        transitionToNextRoute(route, routeParams);
      }
    };

    logPermissionSnapshot();

    initializeOneSignal()
      .then((isInitialized) => {
        if (isInitialized && isActive) {
          OneSignal.Notifications.addEventListener(
            'click',
            handleNotificationClick,
          );
        }
      })
      .catch((error) => {
        reportUnexpectedOneSignalError('click-listener-setup', error);
      });

    return () => {
      isActive = false;
      OneSignal.Notifications.removeEventListener(
        'click',
        handleNotificationClick,
      );
    };
  }, []);

  // login() is what sets the external ID that interface-push-notifications targets. The matching
  // logout() is deliberately not here: it belongs to a real sign out, not to an absent member ID.
  useEffect(() => {
    if (!currentMemberId) return;

    initializeOneSignal()
      .then((isInitialized) => {
        if (isInitialized) {
          OneSignal.login(currentMemberId.toString());
        }
      })
      .catch((error) => {
        reportUnexpectedOneSignalError('login', error);
      });
  }, [currentMemberId]);

  useEffect(() => {
    if (!currentMemberId || !isOnBoardingComplete) return;

    const ensurePushSubscription = async () => {
      const isInitialized = await initializeOneSignal();
      if (!isInitialized) return;

      try {
        // Asking for permission first means only one prompt is raised. optIn() also prompts
        // when permission is missing, so calling both unconditionally races two requests.
        // If the user denies, skip optIn — it can raise a second prompt or open settings.
        let hasPermission = await OneSignal.Notifications.getPermissionAsync();
        if (!hasPermission) {
          hasPermission = await OneSignal.Notifications.requestPermission();
          logNotificationPermissionResult({
            alreadyGranted: false,
            requested: true,
            granted: hasPermission,
          });
        } else {
          logNotificationPermissionResult({
            alreadyGranted: true,
            requested: false,
            granted: true,
          });
        }

        if (
          hasPermission &&
          !(await OneSignal.User.pushSubscription.getOptedInAsync())
        ) {
          OneSignal.User.pushSubscription.optIn();
        }
      } catch (error) {
        telemetry(
          telemetry.ERROR,
          `[useOneSignalSetup] failed to subscribe to push notifications: ${error}`,
        );
      }
    };

    ensurePushSubscription().catch((error) => {
      reportUnexpectedOneSignalError('push-subscribe', error);
    });
  }, [currentMemberId, isOnBoardingComplete]);
}
